<script setup lang="ts">
import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { AreaChartSpec } from "../sbi/protocol";
import { areaGradient, LINE_STROKE_WIDTH, PALETTE } from "../theme.ts";
import { baseOption } from "./echarts-base";
import { autoYDomainFor } from "./chart-yscale.ts";
import { categoryLabelPolicy, plotWidthEstimate } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch — drives margins, legend,
// tick and label policy (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: AreaChartSpec;
    /** Field → format registry; first y field is the fallback lookup key. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const yFields = computed(() =>
  Array.isArray(props.spec.y) ? props.spec.y : [props.spec.y],
);

// `spec.data` is a `ChartData` union (rows-array | SqlMarker |
// DeriveMarker). The InsightChart dispatcher only mounts this
// subcomponent after the marker has resolved — narrow with
// `Array.isArray` so the rest of the component can index/map without
// repeated unions, and so a defective dispatch path can't crash render.
const rows = computed(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const option = computed(() => {
  const base = baseOption({
    title: props.spec.title,
    // Per-axis `yFormat` → per-call `format` (DefaultOpts) → registry
    // format for the first y field.
    yFormat:
      props.spec.yFormat ??
      props.spec.format ??
      props.formats[yFields.value[0]],
    legendNames: yFields.value.length > 1 ? yFields.value : [],
  }, ctx.value);
  const xLabels = rows.value.map((row) => row[props.spec.x]);
  return {
    ...base,
    xAxis: {
      ...base.xAxis,
      type: "category",
      data: xLabels,
      axisLabel: {
        ...base.xAxis.axisLabel,
        ...categoryLabelPolicy({
          labels: xLabels,
          plotWidth: plotWidthEstimate(ctx.value.width),
          mode: "thin",
        }),
      },
    },
    // Auto-scale: when stacked, the visible top is the per-row sum
    // across y-fields; otherwise it's the raw per-row, per-field value.
    // Helper returns `undefined` for zero-range / empty data — fall back
    // to ECharts' "nice" default rather than collapsing the axis.
    yAxis: {
      ...base.yAxis,
      type: "value" as const,
      ...(autoYDomainFor(
        props.spec.yScale,
        props.spec.stacked
          ? rows.value.map((row) => {
              let sum = 0;
              for (const field of yFields.value) {
                const n = Number(row[field]);
                if (Number.isFinite(n)) sum += n;
              }
              return sum;
            })
          : (function* () {
              for (const row of rows.value) {
                for (const field of yFields.value) yield row[field];
              }
            })(),
      ) ?? {}),
    },
    series: yFields.value.map((field, i) => ({
      name: field,
      type: "line",
      // Vertical gradient fill — saturated at the top of each band,
      // fading to near-transparent at the baseline. Matched to the
      // palette colour at the series' index so the legend swatch and
      // stroke colour stay in lockstep.
      areaStyle: { color: areaGradient(i) },
      // Confident stroke in the palette colour sits on top of the
      // gradient and gives each band a crisp upper edge — flat fills
      // alone read as washed out.
      lineStyle: { width: LINE_STROKE_WIDTH, color: PALETTE[i % PALETTE.length] },
      itemStyle: { color: PALETTE[i % PALETTE.length] },
      // Gentle smoothing reads more "modern dashboard" than the
      // default sharp polyline; 0.4 stays close enough to the data
      // shape that it doesn't lie about values between points.
      smooth: 0.4,
      // Per-point dots add visual clutter on top of the gradient fill
      // — emphasis on hover still shows them.
      showSymbol: false,
      symbol: "circle",
      stack: props.spec.stacked ? "total" : undefined,
      data: rows.value.map((row) => row[field]),
    })),
    legend: yFields.value.length > 1 ? base.legend : { show: false },
  };
});

const height = computed(() =>
  resolveChartHeight("area", props.spec.height),
);

// Multi-series area charts tag the datum with `_series` so authors
// can branch on which line was clicked; single-series charts don't
// carry it (no ambiguity to resolve).
function onClick(p: { dataIndex: number }) {
  if (!props.spec.id || !p || typeof p.dataIndex !== "number") return;
  const params = p as { dataIndex: number; seriesName?: string };
  const row = rows.value[params.dataIndex];
  if (!row) return;
  const datum: Record<string, unknown> =
    yFields.value.length > 1 && params.seriesName !== undefined
      ? { ...row, _series: params.seriesName }
      : { ...row };
  emit("click-target", props.spec.id, datum);
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
